import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { adminUsersApi, adminManagementApi, authApi } from '../services/api';
import { isEntraAuth, loginRequest } from '../auth/authConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Access level constants shared by both providers
const ACCESS_LEVELS = {
  'Guest': 0,
  'User': 1,
  'PowerUser': 2,
  'Admin': 3,
  'AppAdmin': 3,
  'SysAdmin': 4
};

// Shared access check helpers
const makeAccessHelpers = (currentUser) => {
  const isSysAdmin = currentUser?.access_Group === 'SysAdmin';
  const isAdmin = isSysAdmin || currentUser?.access_Group === 'Admin' || currentUser?.access_Group === 'AppAdmin';
  const isPowerUser = currentUser?.access_Group === 'PowerUser' || isAdmin;
  const canEdit = (ACCESS_LEVELS[currentUser?.access_Group] ?? 0) >= ACCESS_LEVELS['User'];
  const canConfigure = (ACCESS_LEVELS[currentUser?.access_Group] ?? 0) >= ACCESS_LEVELS['PowerUser'];

  const canAccessModule = (moduleName) => {
    if (!currentUser) return false;
    if (isAdmin) return true;

    const moduleMap = {
      'submittal': 'submittal',
      'estimating': 'estimating',
      'integration': 'integration',
      'purchasing': 'purchasing',
      'transmittal': 'transmittal',
      'qaqc': 'qaqc',
      'commissioning': 'commissioning',
      'sub_contractor': 'sub_Contractor',
      'subcontractor': 'sub_Contractor'
    };

    const propName = moduleMap[moduleName.toLowerCase()];
    if (propName) {
      return currentUser[propName] === true;
    }
    return false;
  };

  const hasAccessLevel = (requiredLevel) => {
    if (!currentUser) return false;
    const userLevel = ACCESS_LEVELS[currentUser.access_Group] ?? 0;
    const required = ACCESS_LEVELS[requiredLevel] ?? 0;
    return userLevel >= required;
  };

  return { isSysAdmin, isAdmin, isPowerUser, canEdit, canConfigure, canAccessModule, hasAccessLevel };
};

// ============================================================
// Impersonation Auth Provider (existing behavior, unchanged)
// ============================================================
export const ImpersonationAuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  // "myIdentity" = who you ARE on this machine (persisted via green check)
  const [myIdentity, setMyIdentity] = useState(null);
  const [hasPersistedIdentity, setHasPersistedIdentity] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadUsers = useCallback(async () => {
    try {
      const res = await adminManagementApi.getUsers();
      const rawUsers = res.data || [];
      const allUsers = rawUsers.map(u => ({
        id: u.id,
        user_Name: u.displayName,
        access_Group: u.role,
        email: u.email,
        azureObjectId: u.azureObjectId,
        isSysAdmin: u.role === 'SysAdmin',
        isActive: u.isActive,
        organizationId: u.organizationId,
        organizationName: u.organizationName
      }));
      setUsers(allUsers);

      // 1. Check for persisted identity ("This is me" — green check)
      const identityId = localStorage.getItem('pmd-identity');
      if (identityId) {
        const identityUser = allUsers.find(u => u.id === parseInt(identityId, 10));
        if (identityUser) {
          setMyIdentity(identityUser);
          setCurrentUser(identityUser);
          setHasPersistedIdentity(true);
          setLoading(false);
          return;
        }
      }

      // 2. No persisted identity — default to first SysAdmin
      const sysAdmin = allUsers.find(u => u.access_Group === 'SysAdmin');
      const fallback = sysAdmin || allUsers.find(u => u.access_Group === 'Admin') || allUsers[0] || null;
      if (fallback) {
        setMyIdentity(fallback);
        setCurrentUser(fallback);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Impersonate — temporarily view as another user
  const impersonate = (user) => {
    setCurrentUser(user);
    setIsImpersonating(user?.id !== myIdentity?.id);
  };

  // Stop impersonating — revert to my identity
  const stopImpersonating = () => {
    setCurrentUser(myIdentity);
    setIsImpersonating(false);
  };

  // Claim identity — "This is me" (green check)
  const claimIdentity = (user) => {
    const target = user || currentUser;
    setMyIdentity(target);
    setCurrentUser(target);
    setIsImpersonating(false);
    setHasPersistedIdentity(true);
    if (target?.id) {
      localStorage.setItem('pmd-identity', target.id.toString());
    }
  };

  const { isSysAdmin, isAdmin, isPowerUser, canEdit, canConfigure, canAccessModule, hasAccessLevel } = makeAccessHelpers(currentUser);

  const value = {
    currentUser,
    realUser: myIdentity,
    isImpersonating,
    hasPersistedIdentity,
    users,
    loading,
    accessDenied: false,
    isSysAdmin,
    isAdmin,
    isPowerUser,
    canEdit,
    canConfigure,
    impersonate,
    stopImpersonating,
    claimIdentity,
    canAccessModule,
    hasAccessLevel,
    refreshUsers: loadUsers
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================
// Entra Auth Provider (Azure AD / MSAL)
// ============================================================
export const EntraAuthProvider = ({ children }) => {
  // These hooks are safe here because EntraAuthProvider is only rendered inside MsalProvider
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [currentUser, setCurrentUser] = useState(null);
  const [realUser, setRealUser] = useState(null); // The actual logged-in user (never changes during impersonation)
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  // Trigger login redirect if not authenticated AND no interaction in progress
  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest).catch((err) => {
        console.error('Login redirect failed:', err);
      });
    }
  }, [isAuthenticated, inProgress, instance]);

  // Once authenticated, resolve identity via /api/auth/me
  const resolveUser = useCallback(async () => {
    if (!isAuthenticated || accounts.length === 0) return;

    try {
      // Load all users from AppSec_Users for admin impersonation
      const usersRes = await adminManagementApi.getUsers();
      const rawUsers = usersRes.data || [];
      const allUsers = rawUsers.map(u => ({
        id: u.id,
        user_Name: u.displayName,
        access_Group: u.role,
        email: u.email,
        azureObjectId: u.azureObjectId,
        isSysAdmin: u.role === 'SysAdmin',
        isActive: u.isActive
      }));
      setUsers(allUsers);

      // Check for active impersonation (admin only - checked after identity resolves)
      const storedImpersonation = localStorage.getItem('pmd-impersonated-user');

      // Resolve Entra identity to PMD user via backend (AppSec_Users is source of truth)
      let meData = null;
      try {
        const meRes = await authApi.getMe();
        meData = meRes.data;
        console.log('[Auth] /api/auth/me resolved:', meData?.displayName, 'role:', meData?.access_Group);
      } catch (err) {
        console.error('[Auth] /api/auth/me FAILED — user will fall back to Guest:', err.message || err);
      }

      // Check if backend says access denied (user not in AppSec_Users)
      if (meData && meData.accessDenied) {
        console.warn('[Auth] Access denied — user not in AppSec_Users. Email:', meData.email);
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      // If /api/auth/me returned a user, use AppSec_Users role as authority
      if (meData && meData.id != null) {
        const resolvedUser = {
          id: meData.id,
          user_Name: meData.displayName,
          access_Group: meData.access_Group,
          isSysAdmin: meData.isSysAdmin ?? false,
          email: meData.email,
          azureObjectId: meData.azureObjectId,
          groups: meData.groups,
          submittal: meData.modules?.submittal ?? false,
          estimating: meData.modules?.estimating ?? false,
          integration: meData.modules?.integration ?? false,
          purchasing: meData.modules?.purchasing ?? false,
          transmittal: meData.modules?.transmittal ?? false,
          qaqc: meData.modules?.qaqc ?? false,
          commissioning: meData.modules?.commissioning ?? false,
          sub_Contractor: meData.modules?.sub_Contractor ?? false,
        };

        // Always set realUser so impersonation can reference the actual logged-in user
        setRealUser(resolvedUser);

        // Check if admin is impersonating someone
        const resolvedHelpers = makeAccessHelpers(resolvedUser);
        if (resolvedHelpers.isAdmin && storedImpersonation) {
          const impersonated = allUsers.find(u => u.id === parseInt(storedImpersonation, 10));
          if (impersonated) {
            setCurrentUser(impersonated);
            setIsImpersonating(true);
            setLoading(false);
            return;
          }
        }
        setCurrentUser(resolvedUser);
      } else {
        // /api/auth/me failed entirely — deny access
        console.error('[Auth] /api/auth/me returned no valid user — access denied');
        setAccessDenied(true);
      }
    } catch (err) {
      console.error('[Auth] Failed to resolve Entra user entirely:', err);
      setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, accounts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    resolveUser();
  }, [resolveUser]);

  const impersonate = (user) => {
    // Only admins can impersonate in Entra mode
    const helpers = makeAccessHelpers(realUser || currentUser);
    if (!helpers.isAdmin) return;

    setCurrentUser(user);
    setIsImpersonating(true);
    if (user?.id) {
      localStorage.setItem('pmd-impersonated-user', user.id.toString());
    }
  };

  const stopImpersonating = () => {
    setCurrentUser(realUser);
    setIsImpersonating(false);
    localStorage.removeItem('pmd-impersonated-user');
  };

  const { isSysAdmin, isAdmin, isPowerUser, canEdit, canConfigure, canAccessModule, hasAccessLevel } = makeAccessHelpers(currentUser);

  const value = {
    currentUser,
    realUser,
    isImpersonating,
    users,
    loading,
    accessDenied,
    isSysAdmin,
    isAdmin,
    isPowerUser,
    canEdit,
    canConfigure,
    impersonate,
    stopImpersonating,
    canAccessModule,
    hasAccessLevel,
    refreshUsers: resolveUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================
// Default AuthProvider - picks based on AUTH_MODE
// (kept for backward compatibility but App.js uses specific ones)
// ============================================================
export const AuthProvider = ({ children }) => {
  if (isEntraAuth()) {
    return <EntraAuthProvider>{children}</EntraAuthProvider>;
  }
  return <ImpersonationAuthProvider>{children}</ImpersonationAuthProvider>;
};

export default AuthContext;
